/**
 * RecipeCard Component
 * 
 * Purpose: Reusable card component for displaying recipe previews in grid layout
 * Features: Photo, name, cook time, rating, tags
 */

import React from 'react';
import config from '../config/environments';

const API_URL = config.api.baseUrl;

const RecipeCard = ({ recipe, onClick }) => {
  // Format cook time (e.g., "45 minutes" or "1 hour 15 minutes")
  const formatCookTime = (minutes) => {
    if (!minutes) return 'Time not specified';
    
    if (minutes < 60) {
      return `${minutes} min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (mins === 0) {
        return `${hours} hr`;
      }
      return `${hours} hr ${mins} min`;
    }
  };

  // Render star rating
  const renderStars = (rating) => {
    if (!rating) return <span className="text-gray-400 text-sm">Not rated</span>;
    
    return (
      <div className="flex items-center">
        {[...Array(5)].map((_, i) => (
          <svg
            key={i}
            className={`w-4 h-4 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        <span className="ml-1 text-sm text-gray-600">{rating}.0</span>
      </div>
    );
  };

  // Get placeholder image if no photo
  const getImageUrl = () => {
    if (recipe.photo_url) {
      return `${API_URL}${recipe.photo_url}`;
    }
    return 'https://via.placeholder.com/400x300?text=No+Photo';
  };

  return (
    <div
      onClick={() => onClick(recipe)}
      className="bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
    >
      {/* Recipe Image */}
      <div className="relative h-48 overflow-hidden bg-gray-200">
        <img
          src={getImageUrl()}
          alt={recipe.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/400x300?text=Recipe+Image';
          }}
        />
        
        {/* Cook Time Badge */}
        {recipe.cook_time_minutes && (
          <div className="absolute top-3 right-3 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-sm font-semibold text-gray-700 shadow-md">
            ⏱️ {formatCookTime(recipe.cook_time_minutes)}
          </div>
        )}
      </div>

      {/* Recipe Details */}
      <div className="p-4">
        {/* Recipe Name */}
        <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2">
          {recipe.name}
        </h3>

        {/* Description */}
        {recipe.description && (
          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
            {recipe.description}
          </p>
        )}

        {/* Rating */}
        <div className="mb-3">
          {renderStars(recipe.admin_rating)}
        </div>

        {/* Tags */}
        {recipe.tags && recipe.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {recipe.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 text-xs font-medium rounded-full"
              >
                {tag}
              </span>
            ))}
            {recipe.tags.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                +{recipe.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Footer Info */}
        <div className="flex items-center justify-between text-xs text-gray-500 mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Serves {recipe.servings || 4}
          </div>
          
          {recipe.prep_time_minutes && (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Prep: {formatCookTime(recipe.prep_time_minutes)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipeCard;

